import { create } from 'zustand';
import { getSocket } from '@/lib/socket';
import { CallPeer, MediaPermissionError, getLocalStream } from '@/lib/call/peer';
import { callsApi, type Call, type CallMode, type IceServer } from '@/api/calls';

// ── Canonical v1 call signaling contract ─────────────────────────────────────
//
// SDP format:   always raw SDP text (e.g. "v=0\r\n..."). No JSON wrapper.
// ICE format:   always RTCIceCandidateInit object { candidate, sdpMid, sdpMLineIndex }.
// Server relay: no fromUserId — peer identity is known from the Call row.

export type CallPhase =
  | 'idle'
  | 'outgoing'        // we initiated; waiting for callee to pick up
  | 'incoming'        // we received an invite; ring UI is on screen
  | 'connecting'      // accepted; negotiating SDP/ICE
  | 'active'          // media flowing
  | 'ended';          // terminal — UI stays briefly then clears

export interface CallUIState {
  call: Call | null;
  phase: CallPhase;
  mode: CallMode;
  error: string | null;
  micEnabled: boolean;
  cameraEnabled: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  peer: CallPeer | null;
  iceServers: IceServer[] | null;
  pendingPeerName: string | null;

  // ── Actions ──────────────────────────────────────────────────────────────
  startCall: (calleeId: string, mode: CallMode, calleeName?: string) => Promise<void>;
  handleIncoming: (call: Call) => void;
  acceptIncoming: () => Promise<void>;
  rejectIncoming: () => void;
  cancelOutgoing: () => void;
  hangup: () => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  onCallState: (call: Call) => Promise<void>;
  onOffer: (callId: string, sdp: string) => Promise<void>;
  onAnswer: (callId: string, sdp: string) => Promise<void>;
  onIceCandidate: (callId: string, candidate: unknown) => Promise<void>;
  onError: (message: string) => void;
  reset: () => void;
  /** Teardown медиа/peer + полный сброс. Для реконсиляции, когда сервер
   *  говорит «активного звонка нет», а локально висит зомби-состояние. */
  forceReset: () => void;
  /** iOS WebKit: системно замьюченный/умерший mic-трек шлёт тишину —
   *  собеседник нас не слышит. Пересоздаёт трек через replaceTrack. */
  recoverMicIfMuted: () => Promise<void>;
}

function initialState() {
  return {
    call: null,
    phase: 'idle' as CallPhase,
    mode: 'VOICE' as CallMode,
    error: null,
    micEnabled: true,
    cameraEnabled: true,
    localStream: null,
    remoteStream: null,
    peer: null,
    iceServers: null as IceServer[] | null,
    pendingPeerName: null as string | null,
  };
}

function isTerminal(status: Call['status']): boolean {
  return (
    status === 'ENDED' ||
    status === 'REJECTED' ||
    status === 'CANCELLED' ||
    status === 'MISSED' ||
    status === 'FAILED'
  );
}

async function ensureIceServers(get: () => CallUIState, set: (partial: Partial<CallUIState>) => void): Promise<IceServer[]> {
  const existing = get().iceServers;
  if (existing) return existing;
  const iceServers = await callsApi.getIceServers();
  set({ iceServers });
  return iceServers;
}

function teardownPeer(state: CallUIState) {
  if (state.peer) {
    try { state.peer.close(); } catch { /* ignore */ }
  }
  if (state.localStream) {
    for (const t of state.localStream.getTracks()) {
      try { t.stop(); } catch { /* ignore */ }
    }
  }
}

/**
 * Подписка на mute/ended локального аудио-трека. iOS WebKit транзиентно
 * мьютит трек при смене аудио-сессии — даём ему 1.5с на самостоятельный
 * unmute, и только потом пытаемся пересоздать. Возвращает функцию,
 * которой надо переподписаться на новый трек после recovery.
 */
function watchMicTrack(get: () => CallUIState) {
  const track = get().localStream?.getAudioTracks()[0];
  if (!track) return;
  const onDegraded = () => {
    setTimeout(() => { void get().recoverMicIfMuted(); }, 1500);
  };
  track.addEventListener('mute', onDegraded);
  track.addEventListener('ended', onDegraded);
}

/**
 * Create a CallPeer wired to the socket for a given call.
 */
function createWiredPeer(
  iceServers: IceServer[],
  callId: string,
  set: (partial: Partial<CallUIState>) => void,
  get: () => CallUIState,
): CallPeer {
  return new CallPeer({
    iceServers,
    onIceCandidate: (candidate) => {
      // Canonical format: RTCIceCandidateInit object
      getSocket().emit('call:ice-candidate', { callId, candidate });
    },
    onTrack: (remoteStream) => set({ remoteStream }),
    onStateChange: (pcState) => {
      if (pcState === 'connected') set({ phase: 'active' });
      if (pcState === 'failed' || pcState === 'closed') {
        const c = get().call;
        if (c && !isTerminal(c.status)) {
          getSocket().emit('call:hangup', { callId: c.id, reason: 'media_error' });
        }
      }
    },
  });
}

export const useCallStore = create<CallUIState>((set, get) => ({
  ...initialState(),

  // ── Outgoing ───────────────────────────────────────────────────────────────
  startCall: async (calleeId, mode, calleeName) => {
    if (get().phase !== 'idle') return;
    set({ error: null, phase: 'outgoing', mode, pendingPeerName: calleeName ?? null });

    let stream: MediaStream;
    try {
      stream = await getLocalStream(mode);
    } catch (err) {
      const code = err instanceof MediaPermissionError ? err.kind : 'other';
      const message =
        code === 'denied'      ? 'Нет доступа к микрофону/камере' :
        code === 'no-device'   ? 'Устройство ввода не найдено' :
        code === 'unsupported' ? 'Браузер не поддерживает WebRTC' :
        'Не удалось получить локальный поток';
      set({ error: message, phase: 'ended' });
      setTimeout(() => get().reset(), 1500);
      return;
    }
    set({ localStream: stream });

    const socket = getSocket();
    socket.emit('call:start', { calleeId, mode });
  },

  // ── Incoming ───────────────────────────────────────────────────────────────
  handleIncoming: (call) => {
    const s = get();
    if (s.phase !== 'idle') {
      // «Занято» — только если реально в живом звонке или дозваниваемся
      // (есть подтверждённый сервером call). Раньше любой не-idle phase
      // приводил к молчаливому auto-reject — на iOS PWA состояние легко
      // застревает после фриза в фоне (повисший getUserMedia → 'outgoing'
      // без call; отменённый звонок при мёртвом сокете → вечный
      // 'incoming'), и дальше ВСЕ входящие тихо отклонялись за <1с.
      const sameCall = s.call?.id === call.id;
      const genuinelyBusy =
        !sameCall &&
        (s.phase === 'active' ||
          s.phase === 'connecting' ||
          (s.phase === 'outgoing' && s.call !== null));
      if (genuinelyBusy) {
        getSocket().emit('call:reject', { callId: call.id });
        return;
      }
      // Зомби-состояние — сбрасываем и принимаем новый звонок. Старому
      // callId reject НЕ шлём: на терминальном звонке бек ответит
      // call:error, который снёс бы UI нового входящего.
      teardownPeer(s);
      get().reset();
    }
    set({
      call,
      phase: 'incoming',
      mode: call.mode,
      error: null,
      micEnabled: true,
      cameraEnabled: true,
    });
  },

  acceptIncoming: async () => {
    const state = get();
    if (state.phase !== 'incoming' || !state.call) return;

    let stream: MediaStream;
    try {
      stream = await getLocalStream(state.mode);
    } catch (err) {
      const code = err instanceof MediaPermissionError ? err.kind : 'other';
      const message =
        code === 'denied'      ? 'Нет доступа к микрофону/камере' :
        code === 'no-device'   ? 'Устройство ввода не найдено' :
        code === 'unsupported' ? 'Браузер не поддерживает WebRTC' :
        'Не удалось получить локальный поток';
      set({ error: message });
      getSocket().emit('call:reject', { callId: state.call.id });
      set({ phase: 'ended' });
      setTimeout(() => get().reset(), 1500);
      return;
    }

    const iceServers = await ensureIceServers(get, set);
    const peer = createWiredPeer(iceServers, state.call.id, set, get);
    peer.attachLocalStream(stream);

    set({
      localStream: stream,
      peer,
      phase: 'connecting',
    });
    watchMicTrack(get);
    getSocket().emit('call:accept', { callId: state.call.id });
  },

  rejectIncoming: () => {
    const state = get();
    if (state.phase !== 'incoming' || !state.call) return;
    getSocket().emit('call:reject', { callId: state.call.id });
    set({ phase: 'ended' });
    setTimeout(() => get().reset(), 800);
  },

  cancelOutgoing: () => {
    const state = get();
    if (!state.call) {
      teardownPeer(state);
      get().reset();
      return;
    }
    getSocket().emit('call:cancel', { callId: state.call.id });
    set({ phase: 'ended' });
    setTimeout(() => get().reset(), 800);
  },

  hangup: () => {
    const state = get();
    if (!state.call) {
      teardownPeer(state);
      get().reset();
      return;
    }
    getSocket().emit('call:hangup', { callId: state.call.id });
    setTimeout(() => {
      const s = get();
      if (s.phase !== 'idle' && s.call?.id === state.call?.id) {
        teardownPeer(s);
        get().reset();
      }
    }, 2000);
  },

  toggleMic: () => {
    const { peer, micEnabled } = get();
    if (!peer) return;
    const next = !micEnabled;
    peer.setMicEnabled(next);
    set({ micEnabled: next });
  },

  toggleCamera: () => {
    const { peer, cameraEnabled } = get();
    if (!peer) return;
    const next = !cameraEnabled;
    peer.setCameraEnabled(next);
    set({ cameraEnabled: next });
  },

  // ── Socket event plumbing ─────────────────────────────────────────────────
  onCallState: async (call) => {
    const prev = get();
    set({ call });

    if (isTerminal(call.status)) {
      teardownPeer(prev);
      set({ phase: 'ended' });
      setTimeout(() => {
        if (get().call?.id === call.id) get().reset();
      }, 1500);
      return;
    }

    if (call.status === 'RINGING' && prev.phase === 'outgoing') {
      return;
    }

    // Callee accepted → caller creates the SDP offer.
    if (call.status === 'ACCEPTED' && prev.phase === 'outgoing') {
      try {
        const iceServers = await ensureIceServers(get, set);
        const peer = createWiredPeer(iceServers, call.id, set, get);
        if (prev.localStream) peer.attachLocalStream(prev.localStream);
        set({ peer, phase: 'connecting' });
        watchMicTrack(get);

        const offer = await peer.createOffer();
        // Canonical: send raw SDP text, no JSON.stringify wrapper
        getSocket().emit('call:offer', { callId: call.id, sdp: offer.sdp! });
      } catch (err) {
        console.error('call: failed to create offer', err);
        getSocket().emit('call:hangup', { callId: call.id, reason: 'media_error' });
      }
    }
  },

  onOffer: async (callId, sdp) => {
    const state = get();
    if (!state.peer || !state.call || state.call.id !== callId) return;
    try {
      // Canonical: sdp is always raw text. Type is implicit = 'offer'.
      const answer = await state.peer.acceptOfferAndAnswer({ type: 'offer', sdp });
      // Canonical: send raw SDP text
      getSocket().emit('call:answer', { callId, sdp: answer.sdp! });
    } catch (err) {
      console.error('call: handling offer failed', err);
      getSocket().emit('call:hangup', { callId, reason: 'media_error' });
    }
  },

  onAnswer: async (callId, sdp) => {
    const state = get();
    if (!state.peer || !state.call || state.call.id !== callId) return;
    try {
      // Canonical: sdp is always raw text. Type is implicit = 'answer'.
      await state.peer.acceptAnswer({ type: 'answer', sdp });
    } catch (err) {
      console.error('call: handling answer failed', err);
      getSocket().emit('call:hangup', { callId, reason: 'media_error' });
    }
  },

  onIceCandidate: async (callId, candidate) => {
    const state = get();
    if (!state.peer || !state.call || state.call.id !== callId) return;
    try {
      // Canonical: candidate is always { candidate, sdpMid, sdpMLineIndex }
      // Backend normalizes before relay, so we can trust the shape.
      const init = candidate as RTCIceCandidateInit;
      await state.peer.addRemoteCandidate(init);
    } catch {
      /* ignore — Chrome sometimes throws on duplicate / null candidates */
    }
  },

  onError: (message) => {
    set({ error: message });
    const state = get();
    teardownPeer(state);
    set({ phase: 'ended' });
    setTimeout(() => {
      if (get().phase === 'ended') get().reset();
    }, 1500);
  },

  reset: () => {
    const { iceServers } = get();
    set({ ...initialState(), iceServers });
  },

  forceReset: () => {
    teardownPeer(get());
    get().reset();
  },

  recoverMicIfMuted: async () => {
    const s = get();
    if (!s.peer || (s.phase !== 'active' && s.phase !== 'connecting')) return;
    const track = s.peer.getLocalAudioTrack();
    // Жив и не замьючен системой — ничего не делаем (track.enabled=false —
    // это НАШ mute-toggle, его не трогаем).
    if (track && track.readyState === 'live' && !track.muted) return;
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const newTrack = fresh.getAudioTracks()[0];
      if (!newTrack) return;
      const cur = get();
      if (!cur.peer || (cur.phase !== 'active' && cur.phase !== 'connecting')) {
        try { newTrack.stop(); } catch { /* ignore */ }
        return;
      }
      newTrack.enabled = cur.micEnabled;
      await cur.peer.replaceAudioTrack(newTrack);
      watchMicTrack(get);
    } catch {
      // Щадяще: не удалось пересоздать (нет permission в фоне и т.п.) —
      // звонок не роняем, попробуем снова на следующем mute/visible.
    }
  },
}));
